import { Box, MenuItem, Select, Stack, Typography } from '@mui/material'
import { Page } from 'components/Page'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import { ErrorIndicator } from 'components/Shared/ErrorIndicator'
import { PageHeader } from 'components/Shared/PageHeader'
import { useCurrentCorporateAccountStore } from 'stores/useCurrentCorporateAccountStore'
import NoPatronsCheckedInIcon from 'assets/NoPatronsCheckedIn.png'
import { NoDataIndicator } from 'components/Shared/NoDataIndicator'
import { useState } from 'react'
import { getLEIdsByPermission } from 'utils/permissions'
import { useSetMe } from 'stores/useMeQuery'
import { CheckboxInput } from 'components/CheckBox/CheckBox'
import { type PerformanceTrendsFilterType } from 'utils/util'
import { intersection, uniqBy } from 'lodash'
import { PerformanceTrendsTable } from './PerformanceTrendsTable'
import { SelectPerformanceTrendsFilterType } from 'components/SelectMonthlyPerformanceReportFilterType'
import { useGetPerformanceTrends } from 'hooks/api/Reports/useGetPerformanceTrendsReport'

type PerformanceTrendsMetricFilter =
  | 'LocationShare'
  | 'CreditsWagered'
  | 'NTI'
  | 'FundsIn'

const defaultMetricFilter: PerformanceTrendsMetricFilter = 'LocationShare'

interface PerformanceTrendsResultsProps {
  selectedLicenses: string[]
  selectedCorporateAccountId: number | undefined
  filterType: PerformanceTrendsFilterType
  selectedMetricFilters: PerformanceTrendsMetricFilter[]
}

const PerformanceTrendsResults = ({
  selectedLicenses,
  selectedCorporateAccountId,
  filterType,
  selectedMetricFilters,
}: PerformanceTrendsResultsProps) => {
  const performanceTrendsQuery = useGetPerformanceTrends({
    licenses: selectedLicenses,
  })

  if (performanceTrendsQuery.isPending && performanceTrendsQuery.isFetching) {
    return <ActivityIndicator />
  }

  if (performanceTrendsQuery.isError) {
    return <ErrorIndicator />
  }

  if (performanceTrendsQuery.data === null) {
    return <Typography variant="h4"></Typography>
  }

  if (!performanceTrendsQuery.data) {
    return null
  }

  return (
    <PerformanceTrendsTable
      data={performanceTrendsQuery.data}
      filterType={filterType}
      selectedMetricFilters={selectedMetricFilters}
      caId={selectedCorporateAccountId}
    />
  )
}

export const PerformanceTrendsTablePage = () => {
  const { passedMe } = useSetMe()
  const meQuery = passedMe
  const [performanceTrendsFilterType, setPerformanceTrendsFilterType] =
    useState<PerformanceTrendsFilterType>('YearToDate')
  const [selectedMetricFilters, setSelectedMetricFilters] = useState<
    PerformanceTrendsMetricFilter[]
  >([defaultMetricFilter])

  const selectedCorporateAccountId =
    useCurrentCorporateAccountStore().currentCorporateAccountId

  const [licensedEstablishmentId, setLicensedEstablishmentId] =
    useState<number>(0)

  const licensedEstablishments = meQuery.data?.licensedEstablishments?.filter(
    (le) => le.corporateAccountId === selectedCorporateAccountId
  )

  const filteredLicensedEstablishments =
    meQuery.data?.licensedEstablishments?.filter(
      (establishment) =>
        establishment.corporateAccountId === selectedCorporateAccountId
    ) ?? []

  // Get LE Ids for the permission the user has access to
  const leIdsByPermissionLocations: number[] = getLEIdsByPermission(
    meQuery.data,
    selectedCorporateAccountId,
    'locationsPermission'
  )

  const leIdsByPermissionReports: number[] = getLEIdsByPermission(
    meQuery.data,
    selectedCorporateAccountId,
    'gamingReportsPermission'
  )

  const leIdsByPermission = intersection(
    leIdsByPermissionLocations,
    leIdsByPermissionReports
  )

  // Filter data by permission
  const licensedEstablishmentsFiltered = filteredLicensedEstablishments?.filter(
    (x) => leIdsByPermission.includes(x.id)
  )

  const selectedLicensedEstablishment = licensedEstablishmentsFiltered.find(
    (le) => le.id === licensedEstablishmentId
  )

  const allLocationLicenses = uniqBy(licensedEstablishmentsFiltered, 'id')
    .map((le) => le.licenseNumber)
    .filter(
      (licenseNumber): licenseNumber is string => licenseNumber !== undefined
    )
    .map((licenseNumber) => String(licenseNumber))

  const selectedLicensesForReport =
    licensedEstablishmentId === 0
      ? allLocationLicenses
      : selectedLicensedEstablishment?.licenseNumber !== undefined
      ? [String(selectedLicensedEstablishment.licenseNumber)]
      : []

  const reportCorporateAccountId =
    licensedEstablishmentId === 0
      ? selectedCorporateAccountId
      : selectedLicensedEstablishment?.corporateAccountId ?? undefined

  const shouldFetchPerformanceTrends = selectedLicensesForReport.length > 0

  const handleCheckboxChange =
    (metric: PerformanceTrendsMetricFilter) => () => {
      setSelectedMetricFilters((previousFilters) =>
        previousFilters.includes(metric)
          ? previousFilters.filter((currentMetric) => currentMetric !== metric)
          : [...previousFilters, metric]
      )
    }

  return (
    <Page
      header={
        <Box>
          <PageHeader
            title="Performance Trends"
            backText="View Reports"
            backPath="/GamingReports"
            isSecondary={true}
          />
        </Box>
      }
    >
      {!licensedEstablishments || licensedEstablishments.length === 0 ? (
        <NoDataIndicator
          imageSource={NoPatronsCheckedInIcon}
          noDataMessage="Cannot view reports because there are no locations available for this corporate account"
        />
      ) : (
        leIdsByPermission.length !== 0 && (
          <Stack spacing={3}>
            {meQuery.isPending && <ActivityIndicator />}
            {meQuery.isError && <ErrorIndicator />}
            <Stack
              direction="row"
              spacing={3}
              alignItems="center"
              flexWrap="nowrap"
              sx={{ overflowX: 'auto', pt: 0.5 }}
            >
              <Select
                displayEmpty
                onChange={(event) => {
                  setLicensedEstablishmentId(Number(event.target.value))
                }}
                value={licensedEstablishmentId}
              >
                <MenuItem value={0}>All locations</MenuItem>
                {uniqBy(
                  licensedEstablishmentsFiltered?.filter(
                    (le) => le.corporateAccountId === selectedCorporateAccountId
                  ),
                  'id'
                )?.map((le) => {
                  return (
                    <MenuItem key={le.id} value={le.id}>
                      {le.standardName} (#{le.licenseNumber})
                    </MenuItem>
                  )
                })}
              </Select>
              <SelectPerformanceTrendsFilterType
                onSelect={setPerformanceTrendsFilterType}
                performanceTrendsType={performanceTrendsFilterType}
                disabledValue={!shouldFetchPerformanceTrends}
              />
              <Stack direction="row" spacing={3} flexWrap="nowrap">
                <CheckboxInput
                  checked={selectedMetricFilters.includes('LocationShare')}
                  text="Location Share"
                  onClick={handleCheckboxChange('LocationShare')}
                  disabled={!shouldFetchPerformanceTrends}
                />

                <CheckboxInput
                  checked={selectedMetricFilters.includes('CreditsWagered')}
                  text="Credits Wagered"
                  onClick={handleCheckboxChange('CreditsWagered')}
                  disabled={!shouldFetchPerformanceTrends}
                />

                <CheckboxInput
                  checked={selectedMetricFilters.includes('NTI')}
                  text="NTI"
                  onClick={handleCheckboxChange('NTI')}
                  disabled={!shouldFetchPerformanceTrends}
                />

                <CheckboxInput
                  checked={selectedMetricFilters.includes('FundsIn')}
                  text="Funds In"
                  onClick={handleCheckboxChange('FundsIn')}
                  disabled={!shouldFetchPerformanceTrends}
                />
              </Stack>
            </Stack>
            {shouldFetchPerformanceTrends && (
              <PerformanceTrendsResults
                selectedLicenses={selectedLicensesForReport}
                selectedCorporateAccountId={reportCorporateAccountId}
                filterType={performanceTrendsFilterType}
                selectedMetricFilters={selectedMetricFilters}
              />
            )}
          </Stack>
        )
      )}
    </Page>
  )
}
